import React, { useEffect, useMemo, useState } from "react";
import { MinusCircle, Search, PackageMinus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { num, fmtDate } from "@/lib/format";
import { Card, Loading, PageHeader, Button, Badge, EmptyState } from "@/components/common";

const TYPE_LABEL = { product: { label: "Produk", c: "bg-primary/10 text-primary" }, material: { label: "Bahan", c: "bg-sky-100 text-sky-700" }, supplies: { label: "Perlengkapan", c: "bg-amber-100 text-amber-700" } };
const kindOf = (r) => (r.item_type === "product" ? "product" : r.category === "supplies" ? "supplies" : "material");

export default function DailyUsage() {
  const { branch } = useAuth();
  const [inv, setInv] = useState(null);
  const [history, setHistory] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [picked, setPicked] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => { if (!branch) return; api.get(`/inventory`, { branch_id: branch.id }).then(setInv).catch(() => setInv([])); api.get(`/inventory/supplies/usage`, { branch_id: branch.id, limit: 50 }).then(setHistory).catch(() => setHistory([])); };
  useEffect(load, [branch]);

  const results = useMemo(() => (inv || []).filter((r) => (type === "all" || kindOf(r) === type) && (q.trim() ? `${r.name} ${r.sku || ""}`.toLowerCase().includes(q.trim().toLowerCase()) : true)).slice(0, 15), [inv, q, type]);

  const submit = async () => {
    if (!picked || !branch) return;
    const n = Number(amount);
    if (!(n > 0)) return toast.error("Isi jumlah pemakaian");
    if (n > picked.qty_physical + 1e-9) return toast.error(`Stok tidak cukup. Tersedia ${num(picked.qty_physical)} ${picked.unit}`);
    if (note.trim().length < 2) return toast.error("Isi keterangan pemakaian");
    setBusy(true);
    try { await api.post("/inventory/supplies/use", { branch_id: branch.id, item_id: picked.item_id, qty: n, note: note.trim() }); toast.success(`${num(n)} ${picked.unit} ${picked.name} tercatat keluar`); setPicked(null); setAmount(""); setNote(""); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const todayTotal = history.filter((h) => new Date(h.created_at).toDateString() === new Date().toDateString()).length;

  return (
    <>
      <PageHeader title="Pemakaian Harian" subtitle="Barang keluar: produk · bahan · perlengkapan toko" icon={PackageMinus} />
      <div className="relative max-w-md mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setPicked(null); }} placeholder="Cari produk / bahan / perlengkapan / SKU…" data-testid="usage-search" className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-300 outline-none focus:border-primary" />
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {[["all", "Semua"], ["product", "Produk"], ["material", "Bahan / Bunga"], ["supplies", "Perlengkapan Toko"]].map(([k, l]) => <button key={k} onClick={() => setType(k)} data-testid={`usage-type-${k}`} className={`px-3 h-9 rounded-full text-sm font-medium ${type === k ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
      </div>

      {picked ? (
        <Card className="p-4 mb-4 border-primary" data-testid="usage-form">
          <div className="flex justify-between items-start">
            <div className="min-w-0"><div className="font-semibold text-slate-800">{picked.name}</div><div className="text-xs text-slate-400">{picked.sku ? `${picked.sku} · ` : ""}stok {num(picked.qty_physical)} {picked.unit}</div></div>
            <Badge className={TYPE_LABEL[kindOf(picked)].c}>{TYPE_LABEL[kindOf(picked)].label}</Badge>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <label className="block"><span className="text-xs font-medium text-slate-500 mb-1 block">Jumlah keluar ({picked.unit})</span><input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0" data-testid="usage-qty" className="w-full h-10 px-3 rounded-xl border border-slate-300 outline-none focus:border-primary" /></label>
            <label className="block col-span-2"><span className="text-xs font-medium text-slate-500 mb-1 block">Keterangan</span><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Dipakai untuk…" data-testid="usage-note" className="w-full h-10 px-3 rounded-xl border border-slate-300 outline-none focus:border-primary" /></label>
          </div>
          <div className="text-xs text-slate-400 mt-2">Sisa setelah simpan: {num(Math.max(0, picked.qty_physical - (Number(amount) || 0)))} {picked.unit}</div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => setPicked(null)}>Batal</Button>
            <Button className="flex-1" onClick={submit} disabled={busy} data-testid="usage-submit"><MinusCircle size={16} /> {busy ? "Menyimpan..." : "Simpan Barang Keluar"}</Button>
          </div>
        </Card>
      ) : (
        <>
          {inv === null ? <Loading /> : results.length === 0 ? <EmptyState icon={Search} title={q ? "Barang tidak ditemukan" : "Belum ada stok"} message="Coba kata kunci lain atau tambah stok." /> : (
            <Card className="overflow-hidden mb-4">
              {results.map((r, i) => (
                <div key={r.item_id} className={`flex items-center gap-3 p-3 ${i > 0 ? "border-t border-slate-50" : ""}`}>
                  <div className="min-w-0 flex-1"><div className="font-medium text-slate-800 truncate">{r.name}</div><div className="flex items-center gap-2 mt-0.5"><Badge className={TYPE_LABEL[kindOf(r)].c}>{TYPE_LABEL[kindOf(r)].label}</Badge><span className="text-xs text-slate-400">{r.sku ? `${r.sku} · ` : ""}stok {num(r.qty_physical)} {r.unit}</span></div></div>
                  <Button variant="outline" onClick={() => setPicked(r)} data-testid={`usage-pick-${r.item_id}`}><MinusCircle size={15} /> Pakai</Button>
                </div>
              ))}
            </Card>
          )}
        </>
      )}

      <h3 className="font-semibold text-slate-700 mb-2">Riwayat Barang Keluar (hari ini {todayTotal})</h3>
      {history.length === 0 ? <p className="text-sm text-slate-400">Belum ada pemakaian tercatat.</p> : (
        <Card className="overflow-hidden" data-testid="usage-history">
          {history.map((h, i) => (
            <div key={h.id} className={`flex items-center justify-between p-3 ${i > 0 ? "border-t border-slate-50" : ""}`}>
              <div className="min-w-0"><div className="font-medium text-slate-800 truncate">{h.item_name}</div><div className="text-xs text-slate-400">{fmtDate(h.created_at)} · {h.created_by_name}{h.note ? ` · ${h.note}` : ""}</div></div>
              <div className="text-right shrink-0"><div className="font-semibold text-rose-600">−{num(h.qty)} {h.unit}</div><div className="text-xs text-slate-400">sisa {num(h.qty_after)}</div></div>
            </div>
          ))}
        </Card>
      )}
    </>
  );
}
